import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TRANSLATE_READY_EVENT,
  applyGoogleTranslation,
  ensureBanglaOnLoad,
  getActiveLanguage,
  getTranslateSelect,
  getUserTranslatePreference,
  refreshGoogleTranslation,
  waitForLanguageChange,
  waitForTranslateSelect,
} from '../../utils/googleTranslate';

const DB_CONTENT_REFRESH_MS = 700;

const GoogleTranslate = () => {
  const location = useLocation();
  const [currentLang, setCurrentLang] = useState('bn');
  const [ready, setReady] = useState(() => Boolean(window.__googleTranslateInitialized));
  const [isTranslating, setIsTranslating] = useState(false);
  const busyRef = useRef(false);
  const routeTimerRef = useRef(null);
  const contentTimerRef = useRef(null);

  useEffect(() => {
    const handleReady = () => {
      ensureBanglaOnLoad();
      setReady(true);
      setCurrentLang('bn');
    };

    if (window.__googleTranslateInitialized) {
      handleReady();
    } else {
      window.addEventListener(TRANSLATE_READY_EVENT, handleReady);
    }

    return () => {
      window.removeEventListener(TRANSLATE_READY_EVENT, handleReady);
    };
  }, []);

  const runTranslation = useCallback(async (lang) => {
    if (busyRef.current) return;

    busyRef.current = true;
    setIsTranslating(true);
    document.body.classList.add('is-translating');

    try {
      if (!getTranslateSelect()) {
        await waitForTranslateSelect();
      }

      await applyGoogleTranslation(lang);
      await waitForLanguageChange(lang, 4000);
      setCurrentLang(getActiveLanguage());

      // Database content may still be loading — translate again once it appears
      if (lang === 'en') {
        setTimeout(refreshGoogleTranslation, DB_CONTENT_REFRESH_MS);
        setTimeout(refreshGoogleTranslation, DB_CONTENT_REFRESH_MS * 3);
      }
    } catch (err) {
      console.error('Translation failed:', err);
      setCurrentLang(getActiveLanguage());
    } finally {
      document.body.classList.remove('is-translating');
      setIsTranslating(false);
      busyRef.current = false;
    }
  }, []);

  const handleToggle = () => {
    if (!ready || isTranslating) return;
    runTranslation(currentLang === 'bn' ? 'en' : 'bn');
  };

  // Re-translate after route changes when user chose English this session
  useEffect(() => {
    if (!ready || getUserTranslatePreference() !== 'en') return undefined;

    clearTimeout(routeTimerRef.current);
    routeTimerRef.current = setTimeout(refreshGoogleTranslation, DB_CONTENT_REFRESH_MS);

    return () => clearTimeout(routeTimerRef.current);
  }, [location.pathname, ready]);

  // Re-translate when database-driven content updates the DOM (menus, pages, etc.)
  useEffect(() => {
    if (!ready || getUserTranslatePreference() !== 'en') return undefined;

    const root = document.getElementById('root');
    if (!root) return undefined;

    const observer = new MutationObserver(() => {
      clearTimeout(contentTimerRef.current);
      contentTimerRef.current = setTimeout(refreshGoogleTranslation, DB_CONTENT_REFRESH_MS);
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      clearTimeout(contentTimerRef.current);
    };
  }, [ready, location.pathname]);

  const label = currentLang === 'bn' ? 'English' : 'Bangla';

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={!ready || isTranslating}
      className="notranslate min-w-[88px] px-3 py-2 border border-green-600 text-nav-green text-sm font-semibold rounded-md hover:bg-green-50 transition-colors whitespace-nowrap disabled:opacity-70 disabled:cursor-wait inline-flex items-center justify-center gap-2"
      translate="no"
      title={currentLang === 'bn' ? 'Translate to English' : 'Translate to Bangla'}
      aria-busy={isTranslating}
      aria-label={isTranslating ? 'Translating' : label}
    >
      {isTranslating ? (
        <>
          <svg
            className="animate-spin h-4 w-4 text-nav-green"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="sr-only">Translating</span>
        </>
      ) : (
        label
      )}
    </button>
  );
};

export default GoogleTranslate;
